import React, { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { Drawer } from "../../../ui/primitives/Drawer";
import { Button } from "../../../ui/primitives/Button";
import { Stack } from "../../../ui/primitives/Stack";
import { Input } from "../../../ui/primitives/Input";
import { Select } from "../../../ui/primitives/Select";
import { DatePicker } from "../../../ui/primitives/DatePicker";
import { Alert } from "../../../ui/primitives/Alert";
import {
  BD_CATEGORIES,
  BD_STATUSES,
  BdOrganisation,
  BdOrganisationStatus,
  SaveBdOrganisationRequest,
} from "../../../model/members/BdOrganisation";
import { StaffUser } from "../../../model/members/StaffUser";
import { toDateOnly } from "../../../utils/DateOnly";
import { fromDateOnly } from "../../../utils/DateOnly";

export interface BdOrganisationPanelProps {
  open: boolean;
  /** The organisation being edited, or null when adding a new one. */
  organisation: BdOrganisation | null;
  staffUsers: StaffUser[];
  saving?: boolean;
  onClose: () => void;
  onSave: (request: SaveBdOrganisationRequest) => void;
}

// fromDateOnly, not bare dayjs(): the API's DateOnly values arrive as midnight UTC, and
// reading them with local calendar fields rolls the day back west of Greenwich — which,
// round-tripped through a save, would decrement the stored date on every edit.
const toDayjs = (value: string | Date | null): Dayjs | null => {
  if (!value) return null;
  const d = fromDateOnly(value);
  return d != null && d.isValid() ? d : null;
};

/**
 * Add or edit an organisation (FORT-362).
 *
 * Only the name is required — an organisation is often created from a single phone call
 * with nothing but a name, and demanding a full contact record at that moment is how
 * organisations end up not being recorded at all.
 */
const BdOrganisationPanel: React.FC<BdOrganisationPanelProps> = ({
  open,
  organisation,
  staffUsers,
  saving,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<string | undefined>();
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<BdOrganisationStatus>("New");
  const [assignedStaffId, setAssignedStaffId] = useState<number | undefined>();
  const [followUpDate, setFollowUpDate] = useState<Dayjs | null>(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reseeded whenever the panel opens on a different record, so the previous organisation's
  // details are never sitting in the form ready to be saved onto this one.
  useEffect(() => {
    if (!open) return;
    setName(organisation?.name ?? "");
    setCategory(organisation?.category ?? undefined);
    setContactName(organisation?.contactName ?? "");
    setPhone(organisation?.phone ?? "");
    setEmail(organisation?.email ?? "");
    setStatus(organisation?.status ?? "New");
    setAssignedStaffId(organisation?.assignedStaffId ?? undefined);
    setFollowUpDate(toDayjs(organisation?.followUpDate ?? null));
    setNotes(organisation?.notes ?? "");
    setError(null);
  }, [open, organisation]);

  const submit = () => {
    if (!name.trim()) {
      setError("An organisation name is required.");
      return;
    }
    setError(null);
    onSave({
      id: organisation?.id ?? null,
      name: name.trim(),
      category: category ?? null,
      contactName: contactName.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      status,
      assignedStaffId: assignedStaffId ?? null,
      // Preserved as-is: this panel doesn't log contact, it edits the record. The
      // "Log contact" action on the list is what stamps last-contacted.
      lastContactedAt: toDateOnly(toDayjs(organisation?.lastContactedAt ?? null)) ?? null,
      followUpDate: toDateOnly(followUpDate) ?? null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={organisation ? "Edit organisation" : "Add organisation"}
      width={460}
      data-testid="bd-organisation-panel"
    >
      <Stack gap={3}>
        {error ? <Alert type="error" message={error} showIcon data-testid="bd-panel-error" /> : null}

        <Input
          label="Organisation name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. King's College London"
          data-testid="bd-panel-name"
        />
        <Select
          label="Category"
          value={category}
          onChange={(v) => setCategory(v as string | undefined)}
          options={BD_CATEGORIES.map((c) => ({ label: c, value: c }))}
          placeholder="Select…"
          allowClear
          data-testid="bd-panel-category"
        />
        <Input
          label="Contact name"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          data-testid="bd-panel-contact"
        />
        <Input
          label="Phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="bd-panel-phone"
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="bd-panel-email"
        />
        <Select
          label="Status"
          value={status}
          onChange={(v) => setStatus((v as BdOrganisationStatus) ?? "New")}
          options={BD_STATUSES.map((s) => ({ label: s, value: s }))}
          data-testid="bd-panel-status"
        />
        <Select
          label="Assigned to"
          value={assignedStaffId}
          onChange={(v) => setAssignedStaffId(v == null ? undefined : Number(v))}
          options={staffUsers.map((s) => ({ label: s.name, value: s.id }))}
          placeholder="Unassigned"
          allowClear
          data-testid="bd-panel-assigned"
        />
        <DatePicker
          label="Follow-up date"
          value={followUpDate}
          onChange={(v) => setFollowUpDate(v)}
          format="DD/MM/YYYY"
          allowClear
          data-testid="bd-panel-followup"
        />
        <Input
          label="Notes"
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes on the organisation, conversation, next steps…"
          data-testid="bd-panel-notes"
        />

        <Button
          variant="primary"
          loading={saving}
          onClick={submit}
          data-testid="bd-panel-save"
        >
          Save organisation
        </Button>
      </Stack>
    </Drawer>
  );
};

export default BdOrganisationPanel;
